import { useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Dumbbell, Plus, Search, Pencil, Trash2, Loader2, Image as ImageIcon, AlertCircle, Upload, X,
} from "lucide-react";
import { ExerciseImage } from "@/components/ExerciseImage";

const MUSCLE_GROUPS = ["Peito", "Costas", "Pernas", "Ombro", "Bíceps", "Tríceps", "Antebraço", "Glúteo", "Panturrilha", "Core/Abdômen"];
const EQUIPMENT = ["Aparelho", "Peso Livre", "Corporal"];


const emptyEx = { name: "", muscle_group: "", equipment: "", description: "", image_url: null };

export default function ExerciseLibrary() {
  const [exercises, setExercises] = useState([]);
  const [group, setGroup] = useState("todos");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyEx);
  const [editId, setEditId] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const imgRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/exercises", { params: { muscle_group: group, search } });
      setExercises(data);
    } catch (_) {
      toast.error("Falha ao carregar exercícios");
    } finally {
      setLoading(false);
    }
  }, [group, search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const openNew = () => { setForm(emptyEx); setEditId(null); setErrors({}); setOpen(true); };
  const openEdit = (ex) => { setForm({ ...emptyEx, ...ex }); setEditId(ex.id); setErrors({}); setOpen(true); };

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const handleImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image_url", data.photo_path);
      toast.success("Imagem enviada!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha no upload");
    } finally {
      setUploadingImg(false);
    }
  };

  const save = async () => {
    const err = {};
    if (!form.name.trim()) err.name = "Nome do exercício é obrigatório";
    if (!form.muscle_group) err.muscle_group = "Selecione o grupo muscular";
    if (!form.equipment) err.equipment = "Selecione o tipo de equipamento";
    setErrors(err);
    if (Object.keys(err).length) return;
    setSaving(true);
    try {
      const payload = { name: form.name, muscle_group: form.muscle_group, equipment: form.equipment, description: form.description, image_url: form.image_url };
      if (editId) await api.put(`/exercises/${editId}`, payload);
      else await api.post("/exercises", payload);
      toast.success(editId ? "Exercício atualizado!" : "Exercício cadastrado!");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (ex) => {
    if (!window.confirm(`Excluir "${ex.name}" da biblioteca?`)) return;
    try {
      await api.delete(`/exercises/${ex.id}`);
      toast.success("Exercício excluído");
      load();
    } catch (_) {
      toast.error("Falha ao excluir");
    }
  };

  const inputCls = (f) => `h-12 bg-[#0F1520] text-slate-100 rounded-xl focus-visible:ring-emerald-500 ${errors[f] ? "border-red-500" : "border-[#263147]"}`;

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input data-testid="exercise-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar exercício..." className="h-12 pl-10 bg-[#181F2C] border-[#263147] text-slate-100 rounded-xl focus-visible:ring-emerald-500" />
        </div>
        <div className="flex gap-3">
          <Select value={group} onValueChange={setGroup}>
            <SelectTrigger data-testid="exercise-group-filter" className="h-12 w-44 bg-[#181F2C] border-[#263147] text-slate-100 rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#181F2C] border-[#263147] text-slate-100">
              <SelectItem value="todos">Todos os grupos</SelectItem>
              {MUSCLE_GROUPS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="new-exercise-button" onClick={openNew} className="h-12 bg-emerald-500 hover:bg-emerald-600 text-[#0A0D12] font-semibold rounded-xl px-5">
            <Plus className="w-4 h-4 mr-2" /> Novo
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-emerald-500 animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="exercise-list">
          {exercises.map((ex) => (
            <div key={ex.id} data-testid={`exercise-card-${ex.id}`} className="bg-[#181F2C] border border-[#263147] rounded-2xl p-4 hover:border-[#34435f] transition-colors group">
              <div className="w-full h-24 rounded-xl bg-[#0F1520] border border-[#263147] flex items-center justify-center mb-3 overflow-hidden">
                {ex.image_url ? <ExerciseImage value={ex.image_url} alt={ex.name} testId={`exercise-card-image-${ex.id}`} className="w-full h-full object-cover rounded-xl" />
                  : <ImageIcon className="w-7 h-7 text-slate-700" />}
              </div>
              <h3 className="font-semibold text-slate-100 leading-tight">{ex.name}</h3>
              <div className="flex gap-1.5 mt-2 flex-wrap">
                <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/15">{ex.muscle_group}</Badge>
                <Badge className="bg-blue-500/15 text-blue-400 border-blue-500/30 hover:bg-blue-500/15">{ex.equipment}</Badge>
              </div>
              {ex.description && <p className="text-xs text-slate-400 mt-2 line-clamp-2">{ex.description}</p>}
              <div className="flex gap-1.5 mt-3">
                <Button size="sm" variant="outline" data-testid={`edit-exercise-${ex.id}`} onClick={() => openEdit(ex)} className="h-8 border-[#263147] bg-[#0F1520] text-slate-200 hover:bg-[#1E2738] rounded-lg">
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="outline" data-testid={`delete-exercise-${ex.id}`} onClick={() => remove(ex)} className="h-8 border-[#263147] bg-[#0F1520] text-red-400 hover:bg-red-500/10 rounded-lg">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#181F2C] border-[#263147] text-slate-100 rounded-2xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Dumbbell className="w-5 h-5 text-emerald-400" /> {editId ? "Editar Exercício" : "Novo Exercício"}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Preencha os dados do exercício da biblioteca.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-slate-300">Nome do exercício *</Label>
              <Input data-testid="exercise-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls("name")} placeholder="Ex: Supino Reto" />
              {errors.name && <p className="text-xs text-red-400 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors.name}</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-slate-300">Grupo muscular *</Label>
                <Select value={form.muscle_group} onValueChange={(v) => set("muscle_group", v)}>
                  <SelectTrigger data-testid="exercise-muscle-select" className={inputCls("muscle_group")}><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent className="bg-[#181F2C] border-[#263147] text-slate-100">
                    {MUSCLE_GROUPS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                  </SelectContent>
                </Select>
                {errors.muscle_group && <p className="text-xs text-red-400">{errors.muscle_group}</p>}
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Equipamento *</Label>
                <Select value={form.equipment} onValueChange={(v) => set("equipment", v)}>
                  <SelectTrigger data-testid="exercise-equipment-select" className={inputCls("equipment")}><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent className="bg-[#181F2C] border-[#263147] text-slate-100">
                    {EQUIPMENT.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                  </SelectContent>
                </Select>
                {errors.equipment && <p className="text-xs text-red-400">{errors.equipment}</p>}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Descrição / Execução</Label>
              <Textarea data-testid="exercise-description-input" value={form.description} onChange={(e) => set("description", e.target.value)}
                className="bg-[#0F1520] border-[#263147] text-slate-100 rounded-xl min-h-[80px]" placeholder="Como executar o movimento..." />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Imagem / Gif de execução</Label>
              <input ref={imgRef} type="file" accept="image/*,image/gif" data-testid="exercise-image-input" onChange={handleImage} className="hidden" />
              {form.image_url ? (
                <div className="flex items-center gap-3">
                  <div className="w-24 h-20 rounded-xl overflow-hidden border border-[#263147] bg-[#0F1520] shrink-0">
                    <ExerciseImage value={form.image_url} alt="Prévia" testId="exercise-form-preview" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={() => imgRef.current?.click()} disabled={uploadingImg}
                      className="h-10 border-[#263147] bg-[#0F1520] text-slate-200 hover:bg-[#1E2738] rounded-xl">
                      {uploadingImg ? <Loader2 className="w-4 h-4 animate-spin" /> : "Trocar"}
                    </Button>
                    <Button type="button" variant="ghost" onClick={() => set("image_url", null)}
                      className="h-10 text-red-400 hover:bg-red-500/10 rounded-xl"><X className="w-4 h-4" /></Button>
                  </div>
                </div>
              ) : (
                <button type="button" data-testid="exercise-image-upload" onClick={() => imgRef.current?.click()} disabled={uploadingImg}
                  className="w-full h-20 rounded-xl border border-dashed border-[#263147] bg-[#0F1520] hover:border-emerald-500/50 hover:bg-[#151d2b] transition-colors flex items-center justify-center gap-2 text-sm text-slate-400">
                  {uploadingImg ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploadingImg ? "Enviando..." : "Enviar foto ou gif de execução"}
                </button>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-[#263147] bg-[#0F1520] text-slate-200 hover:bg-[#1E2738] rounded-xl">Cancelar</Button>
            <Button data-testid="save-exercise-button" onClick={save} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-[#0A0D12] font-semibold rounded-xl">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
